/** AGENDA — semaine et jour.
 *  Sur mobile la semaine est une liste : sept colonnes sur 375 px, ça ne se lit pas.
 *  Sur ordinateur, grille avec glisser-déposer. */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, X } from "lucide-react";
import { api, messageErreur } from "../api";
import { useApp } from "../App";
import { Barre, Btn, Erreur, Micro, Pastille, Squelette, Vide, useBureau } from "../components/Bits";
import {
  STATUTS, ajouterJours, duree, euros, isoJour, jourCourt, jourLong, jourSemaine,
  joursCourts, joursSemaine, lundiDe, minutesDepuisMinuit, moisAnnee, nb,
} from "../lib/format";

const PX_PAR_MIN = 1.1;

/* Un graphique sans phrase actionnable est supprimé. Donc : la phrase. */
function phraseSemaine(semaine) {
  const taux = semaine.remplissage?.taux ?? 0;
  const vides = semaine.jours.filter((j) => j.ouvertures.length && j.rdv.length === 0);
  const ca = semaine.jours.reduce(
    (s, j) => s + j.rdv.reduce((x, r) => x + (r.status === "absent" ? 0 : Number(r.price || 0)), 0),
    0
  );
  const base = `${nb(taux)} % de remplissage, ${euros(ca)} au programme.`;
  if (vides.length === 0) return `${base} Semaine pleine.`;
  if (vides.length === 1)
    return `${base} Ton ${joursSemaine[jourSemaine(vides[0].date)]} est vide.`;
  return `${base} ${vides.length} journées ouvertes sans personne.`;
}

export default function Agenda() {
  const { version, rafraichir, ouvrirRdv } = useApp();
  const bureau = useBureau();
  const [vue, setVue] = useState("semaine");
  const [lundi, setLundi] = useState(lundiDe(isoJour()));
  const [jour, setJour] = useState(isoJour());
  const [semaine, setSemaine] = useState(null);
  const [charge, setCharge] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [glisse, setGlisse] = useState(null);

  const charger = useCallback(async () => {
    setCharge(true);
    setErreur(null);
    try {
      const { data } = await api.get("/semaine", { params: { depuis: lundi } });
      setSemaine(data);
    } catch (e) {
      setErreur(messageErreur(e, "Impossible de charger ton agenda."));
    } finally {
      setCharge(false);
    }
  }, [lundi]);

  useEffect(() => {
    charger();
  }, [charger, version]);

  /* On dépose sur un jour et on garde l'heure : c'est le geste réel. */
  const deposer = async (jourCible) => {
    if (!glisse) return;
    const source = glisse;
    setGlisse(null);
    if (source.jour === jourCible) return;
    const heureIso = new Date(source.start_at);
    const [a, m, j] = jourCible.split("-").map(Number);
    const cible = new Date(heureIso);
    const decalage = Math.round(
      (Date.UTC(a, m - 1, j) -
        Date.UTC(
          Number(source.jour.slice(0, 4)),
          Number(source.jour.slice(5, 7)) - 1,
          Number(source.jour.slice(8, 10))
        )) /
        86400000
    );
    cible.setUTCDate(cible.getUTCDate() + decalage);
    try {
      await api.patch(`/rendez-vous/${source.id}`, { start_at: cible.toISOString() });
      toast.success(`Déplacé au ${jourLong(jourCible)}.`);
      rafraichir();
    } catch (e) {
      toast.error(messageErreur(e, "Déplacement impossible."));
    }
  };

  const jourData = semaine?.jours?.find((j) => j.date === jour);
  const phrase = useMemo(() => (semaine ? phraseSemaine(semaine) : ""), [semaine]);

  return (
    <div data-testid="page-agenda">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Micro>Agenda</Micro>
          <h1
            className="display text-[26px] md:text-[30px] mt-1.5 first-letter:uppercase truncate"
            data-testid="titre-agenda"
          >
            {vue === "semaine" ? moisAnnee(lundi) : jourLong(jour)}
          </h1>
        </div>
        <div
          className="flex shrink-0 p-1 gap-1"
          style={{
            background: "var(--surface-sunken)",
            borderRadius: "var(--radius-full)",
          }}
        >
          {[
            { c: "jour", l: "Jour" },
            { c: "semaine", l: "Semaine" },
          ].map((v) => (
            <button
              key={v.c}
              onClick={() => setVue(v.c)}
              data-testid={`vue-${v.c}`}
              className="h-9 px-4 text-[13px] font-semibold cible-tactile"
              style={{
                borderRadius: "var(--radius-full)",
                background: vue === v.c ? "var(--surface)" : "transparent",
                color: vue === v.c ? "var(--text)" : "var(--text-subtle)",
                boxShadow: vue === v.c ? "var(--shadow-sm)" : "none",
                transition: "background-color var(--dur) var(--ease), color var(--dur) var(--ease)",
              }}
            >
              {v.l}
            </button>
          ))}
        </div>
      </div>

      {semaine && !charge && (
        <p className="text-[16px] md:text-[18px] leading-[1.5] mt-5 max-w-[46ch] pose" data-testid="phrase-semaine">
          {phrase}
        </p>
      )}

      <div className="flex items-center justify-between gap-3 mt-5 mb-4">
        <div className="flex items-center gap-1.5">
          <Fleche
            dir={-1}
            testid="agenda-precedent"
            onClick={() =>
              vue === "semaine" ? setLundi(ajouterJours(lundi, -7)) : setJour(ajouterJours(jour, -1))
            }
          />
          <Fleche
            dir={1}
            testid="agenda-suivant"
            onClick={() =>
              vue === "semaine" ? setLundi(ajouterJours(lundi, 7)) : setJour(ajouterJours(jour, 1))
            }
          />
          <Btn
            variante="nu"
            taille="sm"
            onClick={() => {
              setLundi(lundiDe(isoJour()));
              setJour(isoJour());
            }}
            data-testid="agenda-aujourdhui"
          >
            {vue === "semaine" ? "Cette semaine" : "Aujourd'hui"}
          </Btn>
        </div>
        {semaine && (
          <div className="hidden sm:flex items-center gap-3">
            <span className="num text-[13px]">{nb(semaine.remplissage.taux)} %</span>
            <div className="w-24">
              <Barre pct={semaine.remplissage.taux} />
            </div>
          </div>
        )}
      </div>

      {charge ? (
        <Squelette lignes={4} />
      ) : erreur ? (
        <Erreur message={erreur} surReessai={charger} />
      ) : vue === "semaine" ? (
        bureau ? (
          /* ------------------------------------------------- grille ordinateur */
          <div
            className="overflow-x-auto"
            style={{
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-lg)",
            }}
          >
            <div className="min-w-[860px]">
              <div className="grid grid-cols-7 border-b border-[var(--border)]">
                {semaine.jours.map((j, i) => (
                  <div
                    key={j.date}
                    className="px-3 py-3 border-l border-[var(--border)] first:border-l-0"
                    style={{ background: j.date === isoJour() ? "var(--accent-soft)" : "transparent" }}
                  >
                    <div className="micro micro-muted">{joursCourts[i]}</div>
                    <div className="num text-[17px] mt-0.5">{j.date.slice(8, 10)}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 min-h-[520px]">
                {semaine.jours.map((j) => {
                  const base = j.ouvertures.length
                    ? minutesDepuisMinuit(j.ouvertures[0].debut)
                    : 8 * 60;
                  return (
                    <div
                      key={j.date}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => deposer(j.date)}
                      className="relative border-l border-[var(--border)] first:border-l-0 p-2"
                      style={{
                        background: j.ouvertures.length === 0 ? "var(--surface-sunken)" : "transparent",
                      }}
                    >
                      {j.ouvertures.length === 0 && (
                        <div className="text-[11px] px-1 pt-1" style={{ color: "var(--text-subtle)" }}>
                          Fermé
                        </div>
                      )}
                      {j.rdv.map((r) => (
                        <button
                          key={r.id}
                          draggable
                          onDragStart={() => setGlisse(r)}
                          onClick={() => ouvrirRdv({ rdv: r })}
                          data-testid={`semaine-rdv-${r.id}`}
                          className="rdv-bloc relative w-full text-left px-2.5 py-2 mb-2 cursor-grab active:cursor-grabbing"
                          style={{
                            minHeight: Math.max(40, r.duration_min * PX_PAR_MIN * 0.5),
                            borderLeftColor:
                              r.status === "absent" || r.status === "annule_tardif"
                                ? "var(--danger)"
                                : r.status === "annule"
                                ? "var(--border-strong)"
                                : r.paid
                                ? "var(--success)"
                                : "var(--accent)",
                            opacity: ["annule", "annule_tardif", "absent"].includes(r.status) ? 0.6 : 1,
                          }}
                        >
                          {r.annule_par === "client" && (
                            <span
                              className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center rounded-full"
                              style={{ width: 18, height: 18, background: "var(--danger)", color: "#fff" }}
                              title="La cliente a annulé"
                              data-testid={`badge-annule-client-${r.id}`}
                            >
                              <X size={11} strokeWidth={2.6} />
                            </span>
                          )}
                          <div className="num text-[12px]">{r.heure}</div>
                          <div className="text-[12.5px] font-semibold truncate">{r.client_prenom}</div>
                          <div className="text-[11px] truncate" style={{ color: "var(--text-subtle)" }}>
                            {r.service_nom}
                          </div>
                        </button>
                      ))}
                      <button
                        onClick={() => ouvrirRdv({ jour: j.date })}
                        data-testid={`semaine-ajouter-${j.date}`}
                        aria-label={`Ajouter un rendez-vous le ${jourLong(j.date)}`}
                        className="w-full h-8 inline-flex items-center justify-center"
                        style={{
                          borderRadius: "var(--radius-sm)",
                          border: "1px dashed var(--border-strong)",
                          color: "var(--text-subtle)",
                          transition: "color var(--dur) var(--ease), border-color var(--dur) var(--ease)",
                        }}
                      >
                        <Plus size={14} strokeWidth={1.9} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          /* --------------------------------------------------- liste mobile */
          <div className="space-y-6">
            {semaine.jours.map((j) => (
              <div key={j.date} className="pose">
                <div className="flex items-baseline justify-between pb-2.5 border-b border-[var(--border)]">
                  <div className="flex items-baseline gap-2.5 min-w-0">
                    <span className="num text-[18px]">{j.date.slice(8, 10)}</span>
                    <span className="text-[13.5px] font-semibold first-letter:uppercase truncate">
                      {joursSemaine[jourSemaine(j.date)]}
                    </span>
                    {j.date === isoJour() && <Pastille ton="accent">Aujourd'hui</Pastille>}
                  </div>
                  <button
                    onClick={() => ouvrirRdv({ jour: j.date })}
                    data-testid={`mobile-ajouter-${j.date}`}
                    className="text-[12.5px] font-semibold inline-flex items-center gap-1 shrink-0"
                    style={{ color: "var(--accent)" }}
                  >
                    <Plus size={13} strokeWidth={2.2} /> Ajouter
                  </button>
                </div>
                {j.rdv.length === 0 ? (
                  <p className="text-[13px] py-3" style={{ color: "var(--text-subtle)" }}>
                    {j.ouvertures.length ? "Personne. Le créneau est libre." : "Fermé"}
                  </p>
                ) : (
                  <div className="pt-2 space-y-2">
                    {j.rdv.map((r) => (
                      <button
                        key={r.id}
                        onClick={() => ouvrirRdv({ rdv: r })}
                        data-testid={`mobile-rdv-${r.id}`}
                        className="rdv-bloc w-full text-left flex items-center gap-3 p-3.5"
                        style={{
                          borderLeftColor:
                            r.status === "absent" || r.status === "annule_tardif"
                              ? "var(--danger)"
                              : r.paid
                              ? "var(--success)"
                              : "var(--accent)",
                        }}
                      >
                        <span className="num text-[14px] w-12 shrink-0">{r.heure}</span>
                        {r.annule_par === "client" && (
                          <span
                            className="inline-flex items-center justify-center rounded-full shrink-0"
                            style={{ width: 18, height: 18, background: "var(--danger)", color: "#fff" }}
                            title="La cliente a annulé"
                            data-testid={`badge-annule-client-mobile-${r.id}`}
                          >
                            <X size={11} strokeWidth={2.6} />
                          </span>
                        )}
                        <span className="flex-1 min-w-0">
                          <span className="block text-[15px] font-semibold truncate">
                            {r.client_prenom} {r.client_nom || ""}
                          </span>
                          <span
                            className="block text-[13px] truncate"
                            style={{ color: "var(--text-muted)" }}
                          >
                            {r.service_nom} · {duree(r.duration_min)}
                          </span>
                        </span>
                        <span className="num text-[14px] shrink-0">{euros(r.price)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : (
        /* ------------------------------------------------------------ vue jour */
        <div>
          {!jourData || jourData.rdv.length === 0 ? (
            <Vide
              icone={CalendarDays}
              titre="Rien de prévu ce jour"
              texte="Ajoute un rendez-vous, ou regarde ta semaine pour voir où il te reste de la place."
              action={
                <Btn onClick={() => ouvrirRdv({ jour })} data-testid="jour-vide-ajouter">
                  Ajouter un rendez-vous
                </Btn>
              }
            />
          ) : (
            <div className="timeline pt-2">
              {jourData.rdv.map((r) => (
                <div key={r.id} className="relative flex gap-3 pb-2.5">
                  <div className="timeline-heure text-right pr-3">
                    <div className="num text-[14.5px]">{r.heure}</div>
                    <div className="text-[11px]" style={{ color: "var(--text-subtle)" }}>
                      {r.fin}
                    </div>
                  </div>
                  <span className="pastille" />
                  <button
                    onClick={() => ouvrirRdv({ rdv: r })}
                    data-testid={`jour-rdv-${r.id}`}
                    className="rdv-bloc flex-1 min-w-0 text-left p-3.5 ml-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold truncate">
                          {r.client_prenom} {r.client_nom || ""}
                        </div>
                        <div className="text-[13px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                          {r.service_nom} · {duree(r.duration_min)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="num text-[15px]">{euros(r.price)}</div>
                        <div className="flex justify-end mt-1.5">
                          {r.paid ? (
                            <Pastille ton="olive">Payé</Pastille>
                          ) : (
                            <Pastille ton={STATUTS[r.status]?.ton}>
                              {STATUTS[r.status]?.label}
                            </Pastille>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {bureau && vue === "semaine" && (
        <p className="text-[12px] mt-4" style={{ color: "var(--text-subtle)" }}>
          Fais glisser un rendez-vous sur un autre jour pour le déplacer à la même heure. Servae
          refuse le dépôt si ça chevauche autre chose.
        </p>
      )}
    </div>
  );
}

function Fleche({ dir, onClick, testid }) {
  const I = dir < 0 ? ChevronLeft : ChevronRight;
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-label={dir < 0 ? "Précédent" : "Suivant"}
      className="h-10 w-10 inline-flex items-center justify-center"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        color: "var(--text-muted)",
      }}
    >
      <I size={17} strokeWidth={1.8} />
    </button>
  );
}
